/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import path from 'path';
import fs from 'fs';
import findPodfilePath from './findPodfilePath';
import findXcodeProject from './findXcodeProject';
import findPodspec from './findPodspec';
import findAllPodfilePaths from './findAllPodfilePaths';
import {
  IOSProjectParams,
  IOSDependencyParams,
  IOSProjectConfig,
  IOSDependencyConfig,
} from '@react-native-community/cli-types';

/**
 * Returns project config by analyzing given folder and applying some user defaults
 * when constructing final object
 */
export function projectConfig(
  folder: string,
  userConfig: IOSProjectParams,
): IOSProjectConfig | null {
  if (!userConfig) {
    return null;
  }

  const src = path.join(folder, userConfig.sourceDir ?? '');
  const podfile = findPodfilePath(src);

  /**
   * In certain repos, the Xcode project can be generated by a tool.
   * The only file that we can assume to exist on disk is `Podfile`.
   */
  if (!podfile) {
    return null;
  }

  const sourceDir = path.dirname(podfile);

  const xcodeProject = findXcodeProject(fs.readdirSync(sourceDir));

  return {
    sourceDir,
    xcodeProject,
  };
}

export function dependencyConfig(
  folder: string,
  userConfig: IOSDependencyParams | null = {},
): IOSDependencyConfig | null {
  if (userConfig === null) {
    return null;
  }

  const podspecPath = findPodspec(folder);

  if (!podspecPath) {
    return null;
  }

  return {
    podspecPath,
    configurations: userConfig.configurations || [],
    scriptPhases: userConfig.scriptPhases || [],
  };
}

export const findPodfilePaths = findAllPodfilePaths;
